import { useCallback, useEffect, useState } from "react";
import { useLocationApi } from "@/hooks/apiHooks/useLocationApi";
import { Button, Icon } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import type { LocationFile } from "@models/location";
import type { Location } from "@models/organization";
import { formatDate } from "@utils/formatter";
import {
    acknowledgeErrorMessage,
    downloadErrorMessage,
} from "../_helpers/certReview";
import { useFileDownload } from "../_helpers/useFileDownload";
import { NewCertBadge } from "./NewCertBadge";

interface LocationCertificatesProps {
    // The org-detail row: `hasPendingCertReview` there is what says the newest
    // file is "the new cert"; the files themselves come from GET /locations/{id}.
    location: Location;
    // Re-fetch the org detail after an acknowledge — that is what clears the
    // badge on this row and on the clients list. Its outcome is the parent's
    // to render: the row's own confirmation stands either way.
    onAcknowledged: () => unknown;
}

// One location's resale certificates on the admin client detail (E12-S5 /
// #240): every file on record, NEWEST FIRST, the newest marked while its
// review is pending, a bearer-fetched download per file, and the Acknowledge
// action that clears the pending flag.
export const LocationCertificates = ({
    location,
    onAcknowledged,
}: LocationCertificatesProps) => {
    const { getLocation, acknowledgeLocationCert, downloadLocationFile } =
        useLocationApi();

    // null = not loaded yet (loading or failed); [] = loaded, none on file.
    const [files, setFiles] = useState<LocationFile[] | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);

    const [isAcknowledging, setIsAcknowledging] = useState(false);
    const [acknowledgeError, setAcknowledgeError] = useState<string | null>(null);
    // This row's own record that the POST succeeded. The prop only clears once
    // the parent's refetch lands (and may never, if that read fails), so the
    // button, the highlight and the confirmation key off this the moment the
    // server accepts — a second click cannot 409 a location already acknowledged.
    const [acknowledged, setAcknowledged] = useState(false);

    const {
        downloadingId,
        error: downloadError,
        start: startDownload,
    } = useFileDownload(
        location.id,
        (fileId) => downloadLocationFile(location.id, fileId),
        (error) => downloadErrorMessage(error, "certificates"),
    );

    const fetchFiles = useCallback(async () => {
        setIsLoading(true);
        setLoadFailed(false);

        try {
            const detail = await getLocation(location.id);
            // The server orders newest first; sorting here pins it, because
            // index 0 is what gets marked as the cert under review. By parsed
            // INSTANT, not string order: .NET emits "…T12:00:00Z" and
            // "…T12:00:00.500Z" depending on whether the ticks are zero, and a
            // string compare puts the shorter (earlier-looking) one later. A
            // missing list reads as "none on file", not as nothing at all.
            setFiles(
                [...(detail.files ?? [])].sort(
                    (a, b) => Date.parse(b.uploadedAt) - Date.parse(a.uploadedAt),
                ),
            );
        } catch {
            // A failed refetch must not leave the previous list on screen
            // under the error — null is the documented "not loaded" state.
            setFiles(null);
            setLoadFailed(true);
        } finally {
            setIsLoading(false);
        }
    }, [getLocation, location.id]);

    // Read again when the page's copy of this location changes (its
    // updatedAt): an address edit's change of state files a certificate under
    // it, and the row stays mounted (keyed by id) through the re-read (C6).
    useEffect(() => {
        void fetchFiles();
    }, [fetchFiles, location.updatedAt]);

    // A NEW pending review after this row acknowledged one: the prop went
    // false (the refetch that confirmed the acknowledge) and then true again
    // (a newer upload). Between the POST and that first refetch the prop does
    // not change, so this never undoes a fresh acknowledge.
    useEffect(() => {
        if (location.hasPendingCertReview) {
            setAcknowledged(false);
        }
    }, [location.hasPendingCertReview]);

    const handleAcknowledge = async () => {
        setIsAcknowledging(true);
        setAcknowledgeError(null);

        try {
            await acknowledgeLocationCert(location.id);
        } catch (error) {
            setAcknowledgeError(acknowledgeErrorMessage(error));
            setIsAcknowledging(false);
            return;
        }

        setAcknowledged(true);
        setIsAcknowledging(false);

        // Refresh what was just mutated: this row's files, and the org detail
        // that carries the flag for the row badge and the clients list.
        void fetchFiles();
        onAcknowledged();
    };

    const hasPendingReview = location.hasPendingCertReview && !acknowledged;

    return (
        <div className="mt-2 text-sm">
            {isLoading && (
                <p className="text-neutral/70">Loading certificates…</p>
            )}

            {!isLoading && loadFailed && (
                <p role="alert" className="flex items-center gap-2 text-error">
                    Couldn&apos;t load this location&apos;s certificates.
                    <Button
                        color={ComponentColor.Ghost}
                        size={ComponentSize.XS}
                        className="text-neutral"
                        onClick={() => void fetchFiles()}
                    >
                        Retry
                    </Button>
                </p>
            )}

            {!isLoading && files && files.length === 0 && (
                <p className="text-neutral/70">No certificate on file.</p>
            )}

            {!isLoading && files && files.length > 0 && (
                <ul className="space-y-1">
                    {files.map((file, index) => {
                        const isNewCert = index === 0 && hasPendingReview;

                        return (
                            <li
                                key={file.id}
                                className={`rounded px-2 py-1 ${isNewCert ? "bg-warning/10" : ""}`}
                            >
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <span className="inline-flex items-center gap-2">
                                        <Icon name="description" />
                                        <span
                                            className={
                                                isNewCert ? "font-semibold" : ""
                                            }
                                        >
                                            {file.fileName}
                                        </span>
                                        <span className="text-neutral/70">
                                            Uploaded {formatDate(file.uploadedAt)}
                                        </span>
                                        {isNewCert && <NewCertBadge />}
                                    </span>
                                    <Button
                                        color={ComponentColor.Primary}
                                        size={ComponentSize.XS}
                                        loading={downloadingId === file.id}
                                        disabled={downloadingId !== null}
                                        onClick={() => void startDownload(file)}
                                    >
                                        Download
                                    </Button>
                                </div>
                                {downloadError?.fileId === file.id && (
                                    <p role="alert" className="mt-1 text-error">
                                        {downloadError.message}
                                    </p>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            {hasPendingReview && (
                <div className="mt-2">
                    <Button
                        color={ComponentColor.Success}
                        size={ComponentSize.SM}
                        loading={isAcknowledging}
                        onClick={() => void handleAcknowledge()}
                    >
                        Acknowledge cert
                    </Button>
                    {acknowledgeError && (
                        <p role="alert" className="mt-1 text-error">
                            {acknowledgeError}
                        </p>
                    )}
                </div>
            )}

            {acknowledged && (
                <p role="status" className="mt-2 text-neutral/70">
                    Certificate acknowledged.
                </p>
            )}
        </div>
    );
};
